import React, { useEffect, useContext, useState, useMemo, Dispatch, SetStateAction } from 'react';
import MultiFilter, { StyledChip } from "../MultiFilter";
import { Expert, EntityResponse, SelectedFilters, BasicExpert } from "../../types";
import { LocalizationContext } from "../LanguageContext/LocalizationContext";
import { AutocompleteGetTagProps } from "@material-ui/lab";
import LoadingIndicator from '../UtilComponents/LoadingIndicator';
import { makeStyles, Theme, createStyles } from '@material-ui/core';
import { allSettled } from '../utils';
import { expertStore } from '../../store/stores';
import { filterAndSortExperts } from '../../util/search';
import timanttiService from '../../services/timanttiService';

interface SearchExpertProps {
    selectedExperts: EntityResponse<Expert>[],
    setSelectedFilters: Dispatch<SetStateAction<SelectedFilters>>,
    expertFilters: string[],
    loadingInitialState: boolean
}

const useStyles = makeStyles((_theme: Theme) =>
    createStyles({
        loading: {
            width: "1.5em",
            height: "1.5em",
        }
    })
);

let arrowKeySelectionOn: boolean = false;

const SearchExpertPhysio: React.FC<SearchExpertProps> = props => {
    const { expertFilters, setSelectedFilters, selectedExperts, loadingInitialState } = props;
    const { translation } = useContext(LocalizationContext);
    const [experts, setExperts] = useState<BasicExpert[]>([]);
    const [searchStr, setSearchStr] = useState<string>("");
    const [fetchedExperts, setFetchedExperts] = useState<BasicExpert[] | null>(null);
    const [selection, setSelection] = useState<BasicExpert[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const classes = useStyles();

    useEffect(() => {
        const expertSub = expertStore.subject.subscribe((experts) => {
            const expertList = experts.map(expert => ({
                uuid: expert.uuid,
                Nimi: expert.data.Nimi
            }));
            setExperts(expertList);
            expertList.length !== 0 && setLoading(false);
        });
        return () => expertSub.unsubscribe();
    }, []);

    useEffect(() => {
        if (expertFilters.length !== 0 && !loadingInitialState) {
            const fetchExperts = async () => {
                const resp = await timanttiService.getExperts(expertFilters);
                setFetchedExperts(resp.data.työntekijä);
                setLoading(false);
            };
            fetchExperts();
        }
        if (expertFilters.length === 0) {
            setFetchedExperts(null);
        }
    }, [expertFilters, loadingInitialState]);

    useEffect(() => setLoading(true), [expertFilters]);

    useEffect(() => {
        if (selection) {
            const fetchSelectedExperts = async () => {
                const selectedExperts = await allSettled(
                    selection.map(({ uuid }) => timanttiService.getExpert(uuid))
                );
                const resolvedExperts = selectedExperts.reduce((acc, cur) => {
                    return cur.status === "fulfilled" ? [...acc, cur.value] : acc;
                }, [] as EntityResponse<Expert>[]);
                setSelectedFilters((prevState: SelectedFilters) => ({
                    ...prevState,
                    selectedExperts: resolvedExperts
                }));
            };
            fetchSelectedExperts();
        }
    }, [selection]);

    const expertIsInFilterList = (expert: BasicExpert) =>
        expert && fetchedExperts?.map(expert => expert.uuid)?.includes(expert.uuid) && expert.Nimi;

    const nameMatchesSearchString = (expert: BasicExpert) =>
        expert && expert.Nimi &&
        expert.Nimi.toLowerCase().includes(searchStr.toLowerCase());

    const filteredExperts = experts
        .filter(expert =>
            fetchedExperts && fetchedExperts.length > 0 ?
                expertIsInFilterList(expert) :
                nameMatchesSearchString(expert)
        );

    const onSelectionChange = (e: any, value: BasicExpert[]) => {
        setSelection(value);
        setSearchStr("");
    };

    const renderTags = (
        value: BasicExpert[],
        getTagProps: AutocompleteGetTagProps
    ) =>
        value.map((option, index) => <StyledChip
            label={option.Nimi} {...getTagProps({ index })}
        />
        );

    const filterOptions = (entities: BasicExpert[]) => entities;

    const onInputChange = (e: any) => {
        e.preventDefault();
        const newValue = e.target.value;
        setSearchStr(newValue);
    };

    const filteredSearchExperts = useMemo(
        () => filterAndSortExperts(fetchedExperts, searchStr),
    [fetchedExperts, searchStr]);

    const selectedValues = useMemo(() => selectedExperts.map(expert => ({
        uuid: expert.uuid,
        Nimi: expert.data.Nimi
    })), [selectedExperts]);

    const loadingEl = (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
            {translation.loadingExperts}
            <LoadingIndicator className={classes.loading} />
        </div>
    );

    const onBlur = (e: any) => {
        e.preventDefault();
        setSearchStr("");
        arrowKeySelectionOn = false;
    };

    const handleKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            if (arrowKeySelectionOn) {
                arrowKeySelectionOn = false;
                return;
            } else {
                const input = e.target.value.toLowerCase();
                if (input.length > 3) {
                    if(selection){
                        onSelectionChange(input, filteredSearchExperts.concat(selection));
                    } else {
                        onSelectionChange(input, filteredSearchExperts);
                    }
                }
            }
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            arrowKeySelectionOn = true;
        }
    };

    return (
        <MultiFilter<BasicExpert>
            id={"searchExpert"}
            options={loading ? [] : searchStr.length > 0 ? filteredSearchExperts : filteredExperts}
            label={translation.selectExpert}
            getOptionLabel={option => option.Nimi}
            renderTags={renderTags}
            placeholder={translation.searchExpert}
            onChange={onSelectionChange}
            value={selectedValues}
            filterOptions={filterOptions}
            onInputChange={onInputChange}
            loading={loading}
            loadingText={loadingEl}
            inputStr={searchStr}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
        />
    );
};

export default SearchExpertPhysio;
